import React, { useEffect, useState } from 'react';
import { useTheme } from '../../ThemeContext';
import { apiGet, apiPost, apiDelete, apiPut } from '../../utils/apiClient';

interface VolumeMount {
  name: string;
  share_name: string;
  mount_path: string;
  read_only: boolean;
}

interface ContainerConfig {
  name: string;
  description?: string;
  image: string;
  cpu: number;
  memory_gb: number;
  ports?: number[];
  environment_vars?: { [key: string]: string };
  volume_mounts?: VolumeMount[];
  command?: string[];
  registry_server?: string;
  registry_username?: string;
  registry_password?: string;
}

interface Container {
  container_id: string;
  config: ContainerConfig;
  runtime_status?: any;
  is_running: boolean;
  created_at?: string;
  updated_at?: string;
}

export default function CustomContainersPage() {
  const { theme } = useTheme();
  const [containers, setContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingContainerId, setEditingContainerId] = useState<string | null>(null);
  const [expandedContainerId, setExpandedContainerId] = useState<string | null>(null);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Form state for creating/editing containers
  const [formData, setFormData] = useState<ContainerConfig>({
    name: '',
    description: '',
    image: '',
    cpu: 1.0,
    memory_gb: 1.5,
    ports: [],
    environment_vars: {},
    volume_mounts: [],
    command: [],
    registry_server: '',
    registry_username: '',
    registry_password: '',
  });

  const [portsInput, setPortsInput] = useState('');
  const [envVarKey, setEnvVarKey] = useState('');
  const [envVarValue, setEnvVarValue] = useState('');
  const [commandInput, setCommandInput] = useState('');

  const fetchContainers = async () => {
    try {
      setError(null);
      const response = await apiGet('/api/custom-containers/');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setContainers(data.containers || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load containers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContainers();
    const interval = setInterval(fetchContainers, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const handleCreateContainer = async () => {
    if (!formData.name || !formData.image) {
      alert('Name and Image are required');
      return;
    }

    try {
      setActionInProgress('creating');
      const response = await apiPost('/api/custom-containers/', { config: formData });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      alert(`Container created successfully: ${result.container_id}`);
      setShowCreateForm(false);
      resetForm();
      await fetchContainers();
    } catch (e: any) {
      alert(`Failed to create container: ${e?.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleStartContainer = async (containerId: string) => {
    try {
      setActionInProgress(containerId);
      const response = await apiPost(`/api/custom-containers/${containerId}/start`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      alert(result.message);
      await fetchContainers();
    } catch (e: any) {
      alert(`Failed to start container: ${e?.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleStopContainer = async (containerId: string) => {
    try {
      setActionInProgress(containerId);
      const response = await apiPost(`/api/custom-containers/${containerId}/stop`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      alert(result.message);
      await fetchContainers();
    } catch (e: any) {
      alert(`Failed to stop container: ${e?.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteContainer = async (containerId: string) => {
    if (!window.confirm('Are you sure you want to delete this container?')) {
      return;
    }

    try {
      setActionInProgress(containerId);
      const response = await apiDelete(`/api/custom-containers/${containerId}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      alert('Container deleted successfully');
      await fetchContainers();
    } catch (e: any) {
      alert(`Failed to delete container: ${e?.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleStartEdit = (container: Container) => {
    setEditingContainerId(container.container_id);
    setShowCreateForm(false);
    
    // Populate form with container's config
    setFormData({
      name: container.config.name || '',
      description: container.config.description || '',
      image: container.config.image || '',
      cpu: container.config.cpu || 1.0,
      memory_gb: container.config.memory_gb || 1.5,
      ports: container.config.ports || [],
      environment_vars: container.config.environment_vars || {},
      volume_mounts: container.config.volume_mounts || [],
      command: container.config.command || [],
      registry_server: container.config.registry_server || '',
      registry_username: container.config.registry_username || '',
      registry_password: container.config.registry_password || '',
    });
    
    // Set command input if command exists
    if (container.config.command && container.config.command.length > 0) {
      setCommandInput(container.config.command.join(' '));
    }
  };

  const handleEditContainer = async () => {
    if (!editingContainerId) return;
    
    if (!formData.name || !formData.image) {
      alert('Name and Image are required');
      return;
    }

    try {
      setActionInProgress('editing');
      const response = await apiPut(`/api/custom-containers/${editingContainerId}`, { config: formData });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      alert('Container updated successfully. Stop and restart the container for changes to take effect.');
      setEditingContainerId(null);
      resetForm();
      await fetchContainers();
    } catch (e: any) {
      alert(`Failed to update container: ${e?.message || 'Unknown error'}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingContainerId(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image: '',
      cpu: 1.0,
      memory_gb: 1.5,
      ports: [],
      environment_vars: {},
      volume_mounts: [],
      command: [],
      registry_server: '',
      registry_username: '',
      registry_password: '',
    });
    setPortsInput('');
    setEnvVarKey('');
    setEnvVarValue('');
    setCommandInput('');
  };

  const addPort = () => {
    const port = parseInt(portsInput);
    if (port > 0 && port < 65536) {
      setFormData({
        ...formData,
        ports: [...(formData.ports || []), port],
      });
      setPortsInput('');
    }
  };

  const removePort = (index: number) => {
    const newPorts = [...(formData.ports || [])];
    newPorts.splice(index, 1);
    setFormData({ ...formData, ports: newPorts });
  };

  const addEnvVar = () => {
    if (envVarKey && envVarValue) {
      setFormData({
        ...formData,
        environment_vars: {
          ...(formData.environment_vars || {}),
          [envVarKey]: envVarValue,
        },
      });
      setEnvVarKey('');
      setEnvVarValue('');
    }
  };

  const removeEnvVar = (key: string) => {
    const newEnvVars = { ...(formData.environment_vars || {}) };
    delete newEnvVars[key];
    setFormData({ ...formData, environment_vars: newEnvVars });
  };

  const setCommand = () => {
    if (commandInput) {
      const cmdArray = commandInput.split(' ').filter(s => s.length > 0);
      setFormData({ ...formData, command: cmdArray });
    }
  };

  const containerStyle: React.CSSProperties = {
    background: theme.cardBackground,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: 4,
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    marginRight: 8,
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: theme.buttonBackground,
    color: '#fff',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: theme.secondaryButton,
    color: theme.text,
    border: `1px solid ${theme.border}`,
  };

  const dangerButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: '#dc3545',
    color: '#fff',
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: 4,
    border: `1px solid ${theme.border}`,
    background: theme.inputBackground,
    color: theme.text,
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 500,
    color: theme.text,
  };

  if (loading) {
    return (
      <div style={{ color: theme.text }}>
        <h1>Custom Containers</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ color: theme.text }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Custom Containers</h1>
        <button
          style={primaryButtonStyle}
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            if (showCreateForm) resetForm();
          }}
          disabled={editingContainerId !== null}
        >
          {showCreateForm ? 'Cancel' : 'Create New Container'}
        </button>
      </div>

      {error && (
        <div style={{ ...containerStyle, background: '#fee', color: '#c00', marginBottom: 16 }}>
          Error: {error}
        </div>
      )}

      {(showCreateForm || editingContainerId) && (
        <div style={{ ...containerStyle, marginBottom: 24 }}>
          <h2 style={{ marginTop: 0 }}>
            {editingContainerId ? 'Edit Container' : 'Create New Container'}
          </h2>
          
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Name *</label>
            <input
              style={inputStyle}
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., PostgreSQL Database"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Description</label>
            <input
              style={inputStyle}
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Docker Image *</label>
            <input
              style={inputStyle}
              type="text"
              value={formData.image}
              onChange={(e) => setFormData({ ...formData, image: e.target.value })}
              placeholder="e.g., postgres:15 or nginx:latest"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Registry Server (Optional)</label>
            <input
              style={inputStyle}
              type="text"
              value={formData.registry_server || ''}
              onChange={(e) => setFormData({ ...formData, registry_server: e.target.value })}
              placeholder="e.g., myregistry.azurecr.io"
            />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Leave empty for public Docker Hub images. Required for private registries.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Registry Username (Optional)</label>
              <input
                style={inputStyle}
                type="text"
                value={formData.registry_username || ''}
                onChange={(e) => setFormData({ ...formData, registry_username: e.target.value })}
                placeholder="Registry username"
              />
            </div>
            <div>
              <label style={labelStyle}>Registry Password (Optional)</label>
              <input
                style={inputStyle}
                type="password"
                value={formData.registry_password || ''}
                onChange={(e) => setFormData({ ...formData, registry_password: e.target.value })}
                placeholder="Registry password"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>CPU Cores</label>
              <input
                style={inputStyle}
                type="number"
                step="0.1"
                min="0.1"
                max="4.0"
                value={formData.cpu}
                onChange={(e) => setFormData({ ...formData, cpu: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <label style={labelStyle}>Memory (GB)</label>
              <input
                style={inputStyle}
                type="number"
                step="0.1"
                min="0.5"
                max="16.0"
                value={formData.memory_gb}
                onChange={(e) => setFormData({ ...formData, memory_gb: parseFloat(e.target.value) })}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Ports</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="number"
                value={portsInput}
                onChange={(e) => setPortsInput(e.target.value)}
                placeholder="Port number (e.g., 5432)"
              />
              <button style={secondaryButtonStyle} onClick={addPort}>Add Port</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(formData.ports || []).map((port, idx) => (
                <div key={idx} style={{ 
                  background: theme.buttonBackground, 
                  color: '#fff', 
                  padding: '4px 8px', 
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  {port}
                  <button
                    style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
                    onClick={() => removePort(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Environment Variables</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="text"
                value={envVarKey}
                onChange={(e) => setEnvVarKey(e.target.value)}
                placeholder="Key (e.g., POSTGRES_PASSWORD)"
              />
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="text"
                value={envVarValue}
                onChange={(e) => setEnvVarValue(e.target.value)}
                placeholder="Value"
              />
              <button style={secondaryButtonStyle} onClick={addEnvVar}>Add</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(formData.environment_vars || {}).map(([key, value]) => (
                <div key={key} style={{ 
                  background: theme.secondaryButton,
                  padding: '8px 12px', 
                  borderRadius: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span><strong>{key}</strong>: {value}</span>
                  <button
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18 }}
                    onClick={() => removeEnvVar(key)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Command (optional)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="text"
                value={commandInput}
                onChange={(e) => setCommandInput(e.target.value)}
                placeholder="e.g., python app.py --host 0.0.0.0"
              />
              <button style={secondaryButtonStyle} onClick={setCommand}>Set Command</button>
            </div>
            {formData.command && formData.command.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: theme.textSecondary }}>
                Command: {formData.command.join(' ')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={primaryButtonStyle}
              onClick={editingContainerId ? handleEditContainer : handleCreateContainer}
              disabled={actionInProgress === 'creating' || actionInProgress === 'editing'}
            >
              {actionInProgress === 'creating' ? 'Creating...' : 
               actionInProgress === 'editing' ? 'Updating...' :
               editingContainerId ? 'Update Container' : 'Create Container'}
            </button>
            <button
              style={secondaryButtonStyle}
              onClick={() => {
                if (editingContainerId) {
                  handleCancelEdit();
                } else {
                  setShowCreateForm(false);
                  resetForm();
                }
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <h2>Containers</h2>
      {containers.length === 0 ? (
        <div style={containerStyle}>
          <p>No containers configured. Create one to get started.</p>
        </div>
      ) : (
        containers.map((container) => (
          <div key={container.container_id} style={containerStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0' }}>{container.config.name}</h3>
                {container.config.description && (
                  <p style={{ margin: '0 0 8px 0', color: theme.textSecondary }}>
                    {container.config.description}
                  </p>
                )}
                <div style={{ fontSize: 13, color: theme.textSecondary }}>
                  <div>Image: <code>{container.config.image}</code></div>
                  <div>CPU: {container.config.cpu} cores | Memory: {container.config.memory_gb} GB</div>
                  {container.config.ports && container.config.ports.length > 0 && (
                    <div>Ports: {container.config.ports.join(', ')}</div>
                  )}
                </div>
              </div>
              <div style={{
                padding: '4px 12px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                background: container.is_running ? '#28a745' : '#6c757d',
                color: '#fff'
              }}>
                {container.is_running ? 'RUNNING' : 'STOPPED'}
              </div>
            </div>

            {container.runtime_status && (container.runtime_status.ip_address || container.runtime_status.fqdn) && (
              <div style={{ 
                marginBottom: 12, 
                padding: '8px 12px', 
                background: theme.secondaryButton,
                borderRadius: 4,
                fontSize: 13
              }}>
                {container.runtime_status.fqdn && (
                  <div style={{ marginBottom: 4 }}>
                    FQDN: <strong>{container.runtime_status.fqdn}</strong>
                  </div>
                )}
                {container.runtime_status.ip_address && (
                  <div>
                    IP Address: <strong>{container.runtime_status.ip_address}</strong>
                  </div>
                )}
              </div>
            )}

            {container.runtime_status && container.runtime_status.status !== 'not_found' && (
              <div style={{ marginBottom: 12 }}>
                <button
                  style={{
                    ...secondaryButtonStyle,
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  onClick={() => setExpandedContainerId(
                    expandedContainerId === container.container_id ? null : container.container_id
                  )}
                >
                  <span>Runtime Details</span>
                  <span>{expandedContainerId === container.container_id ? '▼' : '▶'}</span>
                </button>
                
                {expandedContainerId === container.container_id && (
                  <div style={{
                    marginTop: 8,
                    padding: '12px',
                    background: theme.inputBackground,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 4,
                    fontSize: 13,
                  }}>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Container Name:</strong>{' '}
                      <code>{container.runtime_status.container_name || 'N/A'}</code>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Status:</strong>{' '}
                      <span style={{ 
                        color: container.runtime_status.status === 'Running' ? '#28a745' : theme.text 
                      }}>
                        {container.runtime_status.status || 'Unknown'}
                      </span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Provisioning State:</strong>{' '}
                      {container.runtime_status.provisioning_state || 'N/A'}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Location:</strong> {container.runtime_status.location || 'N/A'}
                    </div>
                    {container.runtime_status.fqdn && (
                      <div style={{ marginBottom: 8 }}>
                        <strong>FQDN:</strong> {container.runtime_status.fqdn}
                      </div>
                    )}
                    {container.runtime_status.ip_address && (
                      <div style={{ marginBottom: 8 }}>
                        <strong>IP Address:</strong> {container.runtime_status.ip_address}
                      </div>
                    )}
                    {container.config.ports && container.config.ports.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <strong>Exposed Ports:</strong> {container.config.ports.join(', ')}
                      </div>
                    )}
                    {container.config.environment_vars && Object.keys(container.config.environment_vars).length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <strong>Environment Variables:</strong>
                        <div style={{ marginTop: 4, paddingLeft: 12 }}>
                          {Object.entries(container.config.environment_vars).map(([key, value]) => (
                            <div key={key} style={{ fontSize: 12, color: theme.textSecondary }}>
                              {key}: {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {container.config.command && container.config.command.length > 0 && (
                      <div>
                        <strong>Command:</strong>{' '}
                        <code>{container.config.command.join(' ')}</code>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!container.is_running ? (
                <button
                  style={primaryButtonStyle}
                  onClick={() => handleStartContainer(container.container_id)}
                  disabled={actionInProgress === container.container_id}
                >
                  {actionInProgress === container.container_id ? 'Starting...' : 'Start'}
                </button>
              ) : (
                <button
                  style={secondaryButtonStyle}
                  onClick={() => handleStopContainer(container.container_id)}
                  disabled={actionInProgress === container.container_id}
                >
                  {actionInProgress === container.container_id ? 'Stopping...' : 'Stop'}
                </button>
              )}
              <button
                style={secondaryButtonStyle}
                onClick={() => handleStartEdit(container)}
                disabled={actionInProgress === container.container_id || editingContainerId !== null}
              >
                Edit
              </button>
              <button
                style={dangerButtonStyle}
                onClick={() => handleDeleteContainer(container.container_id)}
                disabled={actionInProgress === container.container_id}
              >
                Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
